import { DestroyRef, inject } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Debounces free-text search input the same way across every server-paginated
 * p-table list in the app: wait 300ms, skip repeated identical terms, and
 * auto-unsubscribe on destroy. Must be called from an injection context
 * (a constructor or field initializer).
 *
 * @param onSearch Called with the debounced term once it settles.
 * @returns A function to feed raw keystrokes into (call it from the input's (input) handler).
 */
export function createSearchDebouncer(
  onSearch: (term: string) => void,
  debounceMs = 300,
): (term: string) => void {
  const destroyRef = inject(DestroyRef);
  const searchInput$ = new Subject<string>();

  searchInput$
    .pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe(onSearch);

  return (term: string) => searchInput$.next(term);
}
